import {
    ChangeDetectorRef,
    Component,
    effect,
    ElementRef,
    Input,
    SimpleChanges,
    ViewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { SelectModule } from 'primeng/select'
import { ButtonModule } from 'primeng/button'
import { ButtonGroupModule } from 'primeng/buttongroup'

import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { CalendarViewMode, CalendarViewService } from '../../services/calendar-view.service'
import { Subject, takeUntil } from 'rxjs'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { DatePickerModule } from 'primeng/datepicker'
import { CommonModule } from '@angular/common'
import { ViewportService } from '@app/core/_services/viewport.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { LocalStorageAccessService } from '@app/core/_services/local-storage-access.service'
import { TimeUtilitiesService } from '@app/core/_services/time-utilities.service'
import { TooltipModule } from 'primeng/tooltip'

interface CalendarViewModeOption {
    label: string
    value: CalendarViewMode
    disabled: boolean
}

// TODO: Replace with design system token when available
const CALENDAR_BUTTONS_TOKEN = {
    root: {
        borderRadius: '8px',
    },
    secondary: {
        background: '#fff',
        color: '#62647E',
        activeBackground: '#f2e8dc',
        borderColor: '#f2e8dc',
    },
    sm: {
        fontSize: '12px',
        fontWeight: 600,
    },
}

@Component({
    selector: 'fm-calendar-header',
    imports: [
        TooltipModule,
        CommonModule,
        DatePickerModule,
        FormsModule,
        SelectModule,
        ButtonModule,
        ButtonGroupModule,
        FontAwesomeModule,
    ],
    templateUrl: './calendar-header.component.html',
    styleUrl: './calendar-header.component.scss',
    //changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarHeaderComponent {
    @Input() timezone: string
    @Input() startOfWeekDay: number
    @Input() scrollBarWidth: number
    private ngUnsubscribe: Subject<any> = new Subject<any>()

    // This hold the month or month range to display
    // on the calendar header
    public dateStringDisplay: string

    // This holds the string used to represent the selected
    // date in the datepicker
    public selectedDateString: string

    faChevronLeft = faChevronLeft
    faChevronRight = faChevronRight
    calendarButtonToken = CALENDAR_BUTTONS_TOKEN

    public maxDaysToShow: number

    public storedCalendarMode: CalendarViewMode
    private storedCalendarModeNumberOfdays: number
    public selectedCalendarMode: CalendarViewMode
    public selectedCalendarModeNumberOfdays: number
    private initialized: boolean

    private lastCheckedDay: moment.Moment

    public calendarViewModeOptions: CalendarViewModeOption[] = [
        { label: '1 day', value: '1d', disabled: false },
        { label: '2 days', value: '2d', disabled: false },
        { label: '3 days', value: '3d', disabled: false },
        { label: '5 days', value: '5d', disabled: false },
        { label: '7 days', value: '7d', disabled: false },
        { label: 'Week', value: '1w', disabled: false },
    ]

    public enableTooltipOndaySelector: boolean = false

    constructor(
        public viewportService: ViewportService,
        private calendarViewService: CalendarViewService,
        private dialogService: DialogService,
        private localStorageAccessService: LocalStorageAccessService,
        private timeUtils: TimeUtilitiesService,
        private cdr: ChangeDetectorRef,
    ) {
        this.lastCheckedDay = moment.tz(this.timezone).startOf('day')

        // set max days to show based on viewport width.
        //  filter the calendar view mode options based on the max days to show.
        effect((onCleanup) => {
            const width = viewportService.width()
            // Defer bound field updates so they do not change during Angular's verification pass.
            const timeoutId = setTimeout(() => {
                const currentMaxDays = this.maxDaysToShow

                this.maxDaysToShow = this.calendarViewService.getMaxDaysToShow(width)

                if (currentMaxDays !== this.maxDaysToShow) {
                    this.calendarViewModeOptions.forEach((option) => {
                        option.disabled =
                            this.calendarViewService.getNumberOfdaysForCalendarMode(option.value) >
                            this.maxDaysToShow
                    })
                    this.enableTooltipOndaySelector = this.calendarViewModeOptions.some(
                        (element) => element.disabled,
                    )
                }
                this.updateCalendarViewModeBasedOnWidth()
                this.cdr.markForCheck()
            })

            onCleanup(() => clearTimeout(timeoutId))
        })
    }

    onSelectDate(event) {
        // User has selected a value from the datepicker, datepicker output is
        // bound to selectedDateString, so go ahead and update the dispay and trigger
        // the calendar range evnet
        this.setDateString()
        this.emitCalendarRange()
    }

    ngOnChanges(changes: SimpleChanges) {
        // Need in case the user updates the start of week day when seeing the
        // start of week day modal on selecting "week" for the first time
        if (changes['startOfWeekDay'] && !changes['startOfWeekDay'].firstChange) {
            if (this.selectedCalendarMode === '1w') {
                this.setDateString()
                this.emitCalendarRange()
            }
        }
    }

    initializeDate() {
        if (this.timezone) {
            //NOTE: Since the application timezone does not have to match the system timezone
            // and since p-datepicker uses Date() instead of a timezone aware value,
            // we must be careful what we pass into the datepicker to ensure it matches what
            // the user expects.
            // In order to do this, we will pass in a string that indicate only what day/month/year
            // is selected (timezone naive)
            const appMoment = moment.tz(this.timezone)
            this.setSelectedDate(appMoment.format('MM.DD.YYYY'))
            this.emitCalendarRange()
        }
    }

    updateCalendarViewModeBasedOnWidth(): void {
        if (!this.maxDaysToShow || !this.storedCalendarMode) {
            return // still initializing
        }
        const daysToShow = Math.min(this.storedCalendarModeNumberOfdays, this.maxDaysToShow)

        // Nothing changed. Exit early, avoids loops
        if (this.selectedCalendarModeNumberOfdays === daysToShow) {
            return
        }

        this.selectedCalendarModeNumberOfdays = daysToShow
        this.selectedCalendarMode = this.calendarViewService.getCalendarModeFromNumberOfDays(
            daysToShow,
            this.storedCalendarMode === '1w',
        )

        if (!this.initialized) {
            this.initialized = true
            this.initializeDate()
        } else {
            this.setDateString()
            this.emitCalendarRange()
        }
    }

    ngOnInit() {
        // Initiallize selected and stored calendar mode data.
        // We need to store each seperately because the effective values might change
        // based on the viewport width, but the stored value only changes when the user
        // explicitly changes the value
        this.storedCalendarMode = this.calendarViewService.getInitialCalendarMode()
        this.storedCalendarModeNumberOfdays =
            this.calendarViewService.getNumberOfdaysForCalendarMode(this.storedCalendarMode)
        this.updateCalendarViewModeBasedOnWidth()

        this.calendarViewService.triggerTodaySelected$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.onClickToday()
            })

        this.timeUtils
            .onIdleMinuteStart()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.onClickToday()
            })
    }

    private setSelectedDate(date: string) {
        this.selectedDateString = date
        this.setDateString()
    }

    private setDateString(): void {
        // The p-datepicker element uses an input element to display the selected
        // date string. There is no way to make that element auto adjust it's width
        // based on what string is being displayed. Make use if the inputStyleClass
        // to set the width based on how long our string is.
        const timezoneMomentStart = moment(this.selectedDateString, 'MM.DD.YYYY')
            .tz(this.timezone)
            .startOf('day')

        // Minus 1 to ensure we only count the days actually shown on the calendar
        const timezoneMomentEnd = timezoneMomentStart
            .clone()
            .add(this.selectedCalendarModeNumberOfdays - 1, 'days')

        const startMonth = timezoneMomentStart.format('MMM')
        const startYear = timezoneMomentStart.format('YYYY')

        const endMonth = timezoneMomentEnd.format('MMM')
        const endYear = timezoneMomentEnd.format('YYYY')

        let displayString: string

        if (timezoneMomentStart.month() === timezoneMomentEnd.month()) {
            displayString = `${startMonth} ${startYear}`
        } else {
            displayString = `${startMonth} - ${endMonth} ${endYear}`
        }
        this.dateStringDisplay = displayString
    }

    onClickToday() {
        this.initializeDate()
        this.calendarViewService.emitTodaySelected()
    }

    onClickAdvanceForward() {
        const m = moment(this.selectedDateString, 'MM.DD.YYYY')
        const mPlusX = m.add(this.selectedCalendarModeNumberOfdays, 'days')
        const resultStr = mPlusX.format('MM.DD.YYYY')
        this.setSelectedDate(resultStr)
        this.emitCalendarRange()
    }

    onClickAdvanceBackwards() {
        const m = moment(this.selectedDateString, 'MM.DD.YYYY')
        const mPlusX = m.subtract(this.selectedCalendarModeNumberOfdays, 'days')
        const resultStr = mPlusX.format('MM.DD.YYYY')
        this.setSelectedDate(resultStr)
        this.emitCalendarRange()
    }

    determineCalendarViewStartDate(): number {
        // given the selected date, and the user's settings, determine which day should be the first in the calendar
        const startOfSelectedDay = moment(this.selectedDateString, 'MM.DD.YYYY')
            .tz(this.timezone)
            .startOf('day')
        if (this.selectedCalendarMode === '1w') {
            if (startOfSelectedDay.day() === 0 && this.startOfWeekDay === 1) {
                // Monday is start of the week and today is Sunday.
                // because of how moment's day() function works below, we move our "today" marker back one week.
                // this ensures that the resulting week will include today.
                startOfSelectedDay.subtract(7, 'days')
            }

            // get sunday or monday of this week
            startOfSelectedDay.day(this.startOfWeekDay)
        }
        return startOfSelectedDay.valueOf()
    }

    emitCalendarRange() {
        this.calendarViewService.emitCalendarRange({
            startTime: this.determineCalendarViewStartDate(),
            numberOfDaysToShow: this.selectedCalendarModeNumberOfdays,
        })
    }

    onChangeNumberOfDaysToShow(event) {
        // User has selected a new value for number of days,
        // save it off in case the viewport size changes and we need
        // to update dynamically the number of days
        this.storedCalendarMode = event.value
        this.storedCalendarModeNumberOfdays =
            this.calendarViewService.getNumberOfdaysForCalendarMode(event.value)

        this.selectedCalendarMode = event.value
        this.selectedCalendarModeNumberOfdays = this.storedCalendarModeNumberOfdays

        this.calendarViewService.storeCalendarMode(event.value)
        this.setDateString()
        this.emitCalendarRange()

        if (this.selectedCalendarMode === '1w' && !this.hasSeenStartOfWeekDialog()) {
            // Show this on first time selecting "week" on the calendar to ensure our
            // users are aware of the setting.
            this.dialogService.openEditStartOfWeekDayDialog(true)
            this.setHasSeenStartOfWeekDialog()
        }
    }

    onCycleNumberOfDays(): void {
        if (this.selectedCalendarModeNumberOfdays === this.maxDaysToShow) {
            this.selectedCalendarModeNumberOfdays = 1
        } else {
            this.selectedCalendarModeNumberOfdays += 1
        }
        this.selectedCalendarMode = this.calendarViewService.getCalendarModeFromNumberOfDays(
            this.selectedCalendarModeNumberOfdays,
            false,
        )

        this.calendarViewService.storeCalendarMode(this.selectedCalendarMode)
        this.setDateString()
        this.emitCalendarRange()
    }

    private addDays(date: Date, days: number): Date {
        const newDate = new Date(date)
        newDate.setDate(newDate.getDate() + days)
        return newDate
    }

    private subtractDays(date: Date, days: number): Date {
        const newDate = new Date(date)
        newDate.setDate(newDate.getDate() - days)
        return newDate
    }

    private hasSeenStartOfWeekDialog(): boolean {
        return this.localStorageAccessService.getItem('viewedDialog:startOfWeekDay') === 'true'
    }

    private setHasSeenStartOfWeekDialog(): void {
        this.localStorageAccessService.setItem('viewedDialog:startOfWeekDay', 'true')
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
